<template>
  <div v-if="visible" class="wizard-overlay">
    <div class="wizard-modal">
      <button @click="close" class="close-btn">
        <i class="fas fa-times"></i>
      </button>

      <div class="wizard-header">
        <h2>
          <i class="fas fa-video"></i>
          Add Video to Course
        </h2>
        <div class="step-indicator">
          <div class="step" :class="{ active: currentStep === 1, completed: currentStep > 1 }">
            <span class="step-number">1</span>
            <span class="step-label">Basic Info</span>
          </div>
          <div class="step-divider"></div>
          <div class="step" :class="{ active: currentStep === 2, completed: currentStep > 2 }">
            <span class="step-number">2</span>
            <span class="step-label">AI Generation</span>
          </div>
          <div v-if="generatedContent" class="step-divider"></div>
          <div v-if="generatedContent" class="step" :class="{ active: currentStep === 3 }">
            <span class="step-number">3</span>
            <span class="step-label">Review & Save</span>
          </div>
        </div>
      </div>

      <div class="wizard-body">
        <!-- Step 1: Basic Information & Vimeo Fetch -->
        <div v-show="currentStep === 1" class="wizard-step">
          <h3>Step 1: Video Information</h3>
          
          <div class="form-group">
            <label>Course *</label>
            <select v-model="formData.course" class="form-select" required>
              <option :value="null">Select a course...</option>
              <option v-for="course in courses" :key="course.id" :value="course.id">
                {{ course.title }}
              </option>
            </select>
          </div>

          <div class="form-group">
            <label>Video Title *</label>
            <input
              v-model="formData.title"
              type="text"
              placeholder="e.g., Ophthalmic Surgery Techniques"
              class="form-input"
              required
            />
          </div>

          <div class="form-group">
            <label>Vimeo URL *</label>
            <div class="vimeo-input-group">
              <input
                v-model="formData.video_url"
                type="url"
                placeholder="https://vimeo.com/123456789"
                class="form-input"
                required
                @blur="extractVimeoId"
              />
              <button
                @click="fetchVimeoMetadata"
                :disabled="fetchingVimeo || !formData.video_url"
                class="btn btn-primary"
                type="button"
              >
                <i :class="fetchingVimeo ? 'fas fa-spinner fa-spin' : 'fas fa-download'"></i>
                {{ fetchingVimeo ? 'Fetching...' : 'Fetch from Vimeo' }}
              </button>
            </div>
            <small>Paste the Vimeo video URL and click "Fetch from Vimeo" to auto-populate duration and metadata</small>
          </div>

          <!-- Vimeo Metadata Display -->
          <div v-if="vimeoMetadata" class="vimeo-metadata">
            <div class="metadata-header">
              <i class="fas fa-check-circle"></i>
              <span>Vimeo Metadata Loaded</span>
            </div>
            <div class="metadata-grid">
              <div class="metadata-item">
                <label>Duration:</label>
                <span>{{ formatDuration(vimeoMetadata.duration) }}</span>
              </div>
              <div class="metadata-item">
                <label>Resolution:</label>
                <span>{{ vimeoMetadata.width }}x{{ vimeoMetadata.height }}</span>
              </div>
              <div class="metadata-item">
                <label>Thumbnail:</label>
                <span v-if="vimeoMetadata.thumbnail_url">✅ Available</span>
                <span v-else>❌ Not available</span>
              </div>
              <div class="metadata-item">
                <label>Transcript:</label>
                <span v-if="vimeoMetadata.transcript_available">✅ Available on Vimeo</span>
                <span v-else class="warning">⚠️ Not available - can generate</span>
              </div>
            </div>
          </div>

          <div class="form-group">
            <label>Description</label>
            <textarea
              v-model="formData.description"
              placeholder="Brief description of the video content..."
              class="form-textarea"
              rows="3"
            ></textarea>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label>Video Type</label>
              <select v-model="formData.video_type" class="form-select">
                <option value="MAIN_CONTENT">Main Content</option>
                <option value="INTRODUCTION">Introduction</option>
                <option value="SUMMARY">Summary</option>
                <option value="Q_AND_A">Q&A Session</option>
                <option value="BONUS">Bonus Content</option>
              </select>
            </div>

            <div class="form-group">
              <label>Order</label>
              <input
                v-model.number="formData.order"
                type="number"
                min="0"
                class="form-input"
              />
            </div>
          </div>
        </div>

        <!-- Step 2: AI Content Generation -->
        <div v-show="currentStep === 2" class="wizard-step">
          <h3>Step 2: AI-Powered Content Generation</h3>
          <p class="step-description">
            Select which content you want AI to generate from the video transcript.
            This will save you 30-45 minutes of manual work!
          </p>

          <!-- Transcript Status -->
          <div class="transcript-status" :class="{ warning: !vimeoMetadata?.transcript_available, success: vimeoMetadata?.transcript_available }">
            <i :class="vimeoMetadata?.transcript_available ? 'fas fa-check-circle' : 'fas fa-exclamation-triangle'"></i>
            <div class="status-content">
              <strong v-if="vimeoMetadata?.transcript_available">
                ✅ Transcript Available - Ready for AI Generation
              </strong>
              <strong v-else>
                ⚠️ Transcript Required for AI Content Generation
              </strong>
              <p v-if="!vimeoMetadata?.transcript_available" class="transcript-help">
                AI needs a transcript to generate summaries, objectives, and quizzes. 
                Click below to generate one from Vimeo (takes 2-5 minutes).
              </p>
              <div v-if="!vimeoMetadata?.transcript_available" class="transcript-actions">
                <button @click="generateTranscript" :disabled="generatingTranscript || !vimeoId" class="btn btn-small btn-warning">
                  <i :class="generatingTranscript ? 'fas fa-spinner fa-spin' : 'fas fa-magic'"></i>
                  {{ generatingTranscript ? 'Requesting Transcript...' : 'Generate Transcript Now' }}
                </button>
                <p v-if="generatingTranscript" class="generating-note">
                  📝 Transcript generation started. This process takes 2-5 minutes. 
                  You can save the video now and come back later to generate AI content.
                </p>
              </div>
            </div>
          </div>

          <!-- Generation Options -->
          <div class="generation-options">
            <h4>What would you like AI to generate?</h4>
            
            <label class="generation-option">
              <input type="checkbox" v-model="generateOptions.summary_paragraph" />
              <div class="option-content">
                <strong>📄 Summary (Paragraph)</strong>
                <small>For course description page - professional 150-200 word summary</small>
              </div>
            </label>

            <label class="generation-option">
              <input type="checkbox" v-model="generateOptions.summary_bullets" />
              <div class="option-content">
                <strong>✓ Summary (Bullet Points)</strong>
                <small>For video player page - 5-7 key topic bullets</small>
              </div>
            </label>

            <label class="generation-option">
              <input type="checkbox" v-model="generateOptions.objectives" />
              <div class="option-content">
                <strong>🎯 Learning Objectives (3-10)</strong>
                <small>Measurable learning goals - "Master X", "Understand Y"</small>
              </div>
            </label>

            <label class="generation-option">
              <input type="checkbox" v-model="generateOptions.quiz" />
              <div class="option-content">
                <strong>❓ Quiz (5 Questions)</strong>
                <small>Moderately difficult multiple choice, 70% to pass, auto-certificate</small>
              </div>
            </label>

            <label class="generation-option">
              <input type="checkbox" v-model="generateOptions.chapters" />
              <div class="option-content">
                <strong>📑 Chapter Markers</strong>
                <small>Timeline navigation with timestamps - jump to key topics</small>
              </div>
            </label>

            <button
              @click="selectAll"
              class="btn btn-secondary btn-small"
              type="button"
            >
              <i class="fas fa-check-double"></i>
              Select All
            </button>
          </div>

        </div>

        <!-- Step 3: Review Generated Content -->
        <div v-show="currentStep === 3" class="wizard-step">
          <h3>Step 3: Review AI-Generated Content</h3>
          <p class="step-description">
            Review and edit the AI-generated content before saving. You can modify any text directly.
          </p>

          <div v-if="generatedContent" class="review-sections">
            
            <!-- Summary Paragraph -->
            <div v-if="generatedContent.summary_paragraph" class="review-section">
              <div class="review-header">
                <h4>
                  <i class="fas fa-align-left"></i>
                  Summary (Paragraph)
                </h4>
                <small>Will be used in course description</small>
              </div>
              <textarea
                v-model="generatedContent.summary_paragraph"
                class="review-textarea"
                rows="6"
              ></textarea>
              <div class="actions-row">
                <div class="char-count">{{ generatedContent.summary_paragraph.length }} characters</div>
                <button @click="copyToCourseSummary" class="btn btn-small btn-primary" type="button">
                  <i class="fas fa-copy"></i>
                  Copy to Course Description
                </button>
              </div>
            </div>

            <!-- Summary Bullets -->
            <div v-if="generatedContent.summary_bullets" class="review-section">
              <div class="review-header">
                <h4>
                  <i class="fas fa-list"></i>
                  Summary (Bullet Points)
                </h4>
                <small>Will be shown on video player page</small>
              </div>
              <div class="bullets-editor">
                <div v-for="(bullet, i) in generatedContent.summary_bullets" :key="i" class="bullet-item">
                  <input v-model="generatedContent.summary_bullets[i]" class="bullet-input" />
                  <button @click="generatedContent.summary_bullets.splice(i, 1)" class="remove-btn">
                    <i class="fas fa-times"></i>
                  </button>
                </div>
                <button @click="generatedContent.summary_bullets.push('')" class="add-btn">
                  <i class="fas fa-plus"></i>
                  Add Bullet
                </button>
              </div>
            </div>

            <!-- Learning Objectives -->
            <div v-if="generatedContent.objectives" class="review-section">
              <div class="review-header">
                <h4>
                  <i class="fas fa-bullseye"></i>
                  Learning Objectives ({{ generatedContent.objectives.length }})
                </h4>
                <small>What students will learn</small>
              </div>
              <div class="objectives-editor">
                <div v-for="(obj, i) in generatedContent.objectives" :key="i" class="objective-item">
                  <span class="objective-number">{{ i + 1 }}.</span>
                  <input v-model="generatedContent.objectives[i]" class="objective-input" />
                  <button @click="generatedContent.objectives.splice(i, 1)" class="remove-btn">
                    <i class="fas fa-times"></i>
                  </button>
                </div>
                <button @click="generatedContent.objectives.push('')" class="add-btn">
                  <i class="fas fa-plus"></i>
                  Add Objective
                </button>
              </div>
              <div class="section-footer">
                <button @click="copyToCourseObjectives" class="btn btn-small btn-primary" type="button">
                  <i class="fas fa-copy"></i>
                  Copy to Course Learning Objectives
                </button>
              </div>
            </div>

            <!-- Quiz Questions -->
            <div v-if="generatedContent.quiz" class="review-section">
              <div class="review-header">
                <h4>
                  <i class="fas fa-question-circle"></i>
                  Quiz Questions ({{ generatedContent.quiz.length }})
                </h4>
                <small>Moderately difficult, 70% to pass</small>
              </div>
              <div class="quiz-editor">
                <div v-for="(question, i) in generatedContent.quiz" :key="i" class="quiz-question">
                  <div class="question-header">
                    <strong>Question {{ i + 1 }}</strong>
                    <button @click="generatedContent.quiz.splice(i, 1)" class="remove-btn">
                      <i class="fas fa-trash"></i>
                    </button>
                  </div>
                  <input v-model="question.question" class="question-input" placeholder="Question text" />
                  <div class="answers-list">
                    <div v-for="(answer, ai) in question.answers" :key="ai" class="answer-item">
                      <input
                        type="radio"
                        :name="`correct-${i}`"
                        :checked="question.correct === ai"
                        @change="question.correct = ai"
                      />
                      <input v-model="question.answers[ai]" class="answer-input" :placeholder="`Answer ${String.fromCharCode(65 + ai)}`" />
                    </div>
                  </div>
                  <div v-if="question.explanation" class="explanation">
                    <label>Explanation:</label>
                    <textarea v-model="question.explanation" rows="2" class="explanation-textarea"></textarea>
                  </div>
                </div>
              </div>
            </div>

            <!-- Chapters -->
            <div v-if="generatedContent.chapters" class="review-section">
              <div class="review-header">
                <h4>
                  <i class="fas fa-bookmark"></i>
                  Chapter Markers ({{ generatedContent.chapters.length }})
                </h4>
                <small>Timeline navigation points</small>
              </div>
              <div class="chapters-editor">
                <div v-for="(chapter, i) in generatedContent.chapters" :key="i" class="chapter-item">
                  <input v-model.number="chapter.time" type="number" class="time-input" placeholder="Seconds" />
                  <input v-model="chapter.title" class="chapter-input" placeholder="Chapter title" />
                  <button @click="generatedContent.chapters.splice(i, 1)" class="remove-btn">
                    <i class="fas fa-times"></i>
                  </button>
                </div>
                <button @click="generatedContent.chapters.push({time: 0, title: ''})" class="add-btn">
                  <i class="fas fa-plus"></i>
                  Add Chapter
                </button>
              </div>
            </div>

          </div>
        </div>
      </div>

      <div class="wizard-footer">
        <button v-if="currentStep > 1" @click="previousStep" class="btn btn-secondary">
          <i class="fas fa-arrow-left"></i>
          Back
        </button>
        
        <div class="footer-actions">
          <button @click="close" class="btn btn-secondary">
            Cancel
          </button>
          
          <button
            v-if="currentStep === 1"
            @click="nextStep"
            :disabled="!canProceedStep1"
            class="btn btn-primary"
          >
            Next: AI Generation
            <i class="fas fa-arrow-right"></i>
          </button>

          <button
            v-if="currentStep === 2"
            @click="generateContent"
            :disabled="generating || !hasAnyOptionSelected"
            class="btn btn-success"
          >
            <i :class="generating ? 'fas fa-spinner fa-spin' : 'fas fa-robot'"></i>
            {{ generating ? 'Generating...' : 'Generate AI Content' }}
          </button>

          <button
            v-if="currentStep === 2 && !generating"
            @click="saveWithoutGeneration"
            class="btn btn-secondary"
          >
            <i class="fas fa-save"></i>
            Skip AI - Save Manually
          </button>

          <button
            v-if="currentStep === 3"
            @click="saveWithGeneratedContent"
            class="btn btn-success"
          >
            <i class="fas fa-save"></i>
            Save Video & Content
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed } from 'vue'
import { lmsAPI } from '@/services/lmsAPI.js'
import { useToast } from 'vue-toastification'

export default {
  name: 'VideoCreationWizard',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    courses: {
      type: Array,
      required: true
    }
  },
  emits: ['close', 'video-created'],
  setup(props, { emit }) {
    const toast = useToast()
    
    const currentStep = ref(1)
    const fetchingVimeo = ref(false)
    const generating = ref(false)
    const generatingTranscript = ref(false)
    const createdVideoId = ref(null)  // Store video ID after creation
    
    const formData = ref({
      course: null,
      title: '',
      description: '',
      video_url: '',
      video_type: 'MAIN_CONTENT',
      order: 0,
      duration_seconds: null
    })
    
    const vimeoMetadata = ref(null)
    const vimeoId = ref(null)
    
    const generateOptions = ref({
      summary_paragraph: true,
      summary_bullets: true,
      objectives: true,
      quiz: true,
      chapters: true
    })
    
    const generatedContent = ref(null)
    
    const canProceedStep1 = computed(() => {
      return formData.value.course && 
             formData.value.title && 
             formData.value.video_url &&
             vimeoMetadata.value  // Must fetch Vimeo data first
    })
    
    const hasAnyOptionSelected = computed(() => {
      return Object.values(generateOptions.value).some(v => v === true)
    })
    
    const extractVimeoId = () => {
      if (!formData.value.video_url) return
      
      const patterns = [
        /vimeo\.com\/(\d+)/,
        /player\.vimeo\.com\/video\/(\d+)/,
        /\/videos\/(\d+)/
      ]
      
      for (const pattern of patterns) {
        const match = formData.value.video_url.match(pattern)
        if (match) {
          vimeoId.value = match[1]
          return match[1]
        }
      }
      
      if (/^\d+$/.test(formData.value.video_url)) {
        vimeoId.value = formData.value.video_url
        return formData.value.video_url
      }
      
      return null
    }
    
    const fetchVimeoMetadata = async () => {
      const id = extractVimeoId()
      if (!id) {
        toast.error('Invalid Vimeo URL')
        return
      }
      
      fetchingVimeo.value = true
      try {
        const response = await lmsAPI.fetchVimeoMetadata({
          vimeo_url: formData.value.video_url
        })
        
        vimeoMetadata.value = response.data
        
        // Auto-populate fields
        if (response.data.duration) {
          formData.value.duration_seconds = response.data.duration
        }
        if (response.data.title && !formData.value.title) {
          formData.value.title = response.data.title
        }
        if (response.data.description && !formData.value.description) {
          formData.value.description = response.data.description
        }
        
        toast.success('Vimeo metadata loaded successfully!')
      } catch (error) {
        console.error('Error fetching Vimeo metadata:', error)
        toast.error('Failed to fetch Vimeo metadata. Please check the URL and try again.')
      } finally {
        fetchingVimeo.value = false
      }
    }
    
    const generateTranscript = async () => {
      if (!vimeoId.value) {
        toast.error('No Vimeo ID found. Please fetch Vimeo metadata first.')
        return
      }
      
      generatingTranscript.value = true
      try {
        toast.info('🎬 Requesting transcript generation from Vimeo...', { timeout: 3000 })
        
        // Call Vimeo to generate transcript (uses existing analyze_transcript endpoint pattern)
        const response = await lmsAPI.generateVimeoTranscript({
          vimeo_id: vimeoId.value
        })
        
        if (response.data.status === 'processing') {
          toast.success('📝 Transcript generation started! This takes 2-5 minutes. You can save the video and come back later.', { timeout: 8000 })
          vimeoMetadata.value.transcript_status = 'processing'
        } else if (response.data.status === 'completed' || response.data.transcript) {
          vimeoMetadata.value.transcript_available = true
          toast.success('✅ Transcript ready! You can now generate AI content.', { timeout: 5000 })
        }
      } catch (error) {
        console.error('Error generating transcript:', error)
        const errorMsg = error.response?.data?.error || error.response?.data?.message || error.message
        toast.error('Failed to generate transcript: ' + errorMsg, { timeout: 10000 })
        
        // Provide helpful guidance
        toast.info('💡 Tip: You can save the video now and use the "AI Analyze" button from the video list to generate transcript later.', { timeout: 8000 })
      } finally {
        generatingTranscript.value = false
      }
    }
    
    const nextStep = () => {
      if (currentStep.value === 1 && canProceedStep1.value) {
        currentStep.value = 2
      }
    }
    
    const previousStep = () => {
      if (currentStep.value > 1) {
        currentStep.value--
      }
    }
    
    const selectAll = () => {
      const allSelected = hasAnyOptionSelected.value && 
                         Object.values(generateOptions.value).every(v => v === true)
      
      Object.keys(generateOptions.value).forEach(key => {
        generateOptions.value[key] = !allSelected
      })
    }
    
    const generateContent = async () => {
      // Just generate content, don't save yet (go to review step)
      generating.value = true
      try {
        // Step 1: Create the video first
        const videoData = {
          course: formData.value.course,
          title: formData.value.title,
          description: formData.value.description,
          video_url: formData.value.video_url,
          video_type: formData.value.video_type,
          order: formData.value.order,
          duration_seconds: formData.value.duration_seconds,
          processing_status: 'COMPLETED'
        }
        
        const videoResponse = await lmsAPI.createVideo(videoData)
        const createdVideo = videoResponse.data
        
        console.log('Created video response:', createdVideo)
        
        // Ensure we have the video ID
        const videoId = createdVideo.id || createdVideo.pk
        if (!videoId) {
          throw new Error('Video created but ID not returned. Response: ' + JSON.stringify(createdVideo))
        }
        
        // Store video ID for Step 3 save
        createdVideoId.value = videoId
        
        toast.success('Video created!')
        
        // Step 2: Generate AI content if any options selected
        if (hasAnyOptionSelected.value) {
          // Check if video has transcript
          if (!vimeoMetadata.value?.transcript_available) {
            toast.warning('Video has no transcript. AI generation skipped. Use "AI Analyze" button on the video to generate transcript first.', { timeout: 8000 })
            emit('video-created', createdVideo)
            close()
            return
          }
          
          toast.info('Generating AI content... This may take 30-60 seconds.', { timeout: 3000 })
          
          const selectedOptions = Object.keys(generateOptions.value)
            .filter(key => generateOptions.value[key])
          
          console.log('Generating content for video ID:', videoId, 'Options:', selectedOptions)
          
          const contentResponse = await lmsAPI.generateCourseContent(
            videoId,
            { generate: selectedOptions }
          )
          
          generatedContent.value = contentResponse.data
          
          toast.success('AI content generated successfully!', { timeout: 5000 })
          
          // Go to review step
          currentStep.value = 3
        } else {
          // No AI generation requested, just save
          emit('video-created', createdVideo)
          toast.success(`Video "${formData.value.title}" added successfully!`, { timeout: 3000 })
          close()
        }
        
      } catch (error) {
        console.error('Error in generation/save:', error)
        const errorMsg = error.response?.data?.error || error.message || 'Failed to create video'
        toast.error(errorMsg, { timeout: 10000 })
        alert('Error:\n\n' + errorMsg)
      } finally {
        generating.value = false
      }
    }
    
    const copyToCourseSummary = async () => {
      try {
        const courseId = formData.value.course
        
        // Fetch course first to get current values and avoid validation errors
        const courseResponse = await lmsAPI.getCourse(courseId)
        const course = courseResponse.data
        
        // Update with just the description change
        const updateData = {
          title: course.title,
          slug: course.slug,
          description: generatedContent.value.summary_paragraph,  // Updated field
          short_description: course.short_description || '',
          course_type: course.course_type,
          difficulty_level: course.difficulty_level,
          status: course.status,
          category: typeof course.category === 'object' ? course.category?.id : course.category,
          is_public: course.is_public || false
        }
        
        await lmsAPI.updateCourse(courseId, updateData)
        toast.success('✅ Copied to Course Description!', { timeout: 3000 })
      } catch (error) {
        console.error('Error updating course:', error)
        const errorMsg = error.response?.data?.error || error.message
        toast.error('Failed to update course: ' + errorMsg)
      }
    }
    
    const copyToCourseObjectives = async () => {
      try {
        const courseId = formData.value.course
        
        // Fetch course first
        const courseResponse = await lmsAPI.getCourse(courseId)
        const course = courseResponse.data
        
        // Update with objectives
        const updateData = {
          title: course.title,
          slug: course.slug,
          description: course.description || '',
          short_description: course.short_description || '',
          learning_objectives: generatedContent.value.objectives,  // Updated field
          course_type: course.course_type,
          difficulty_level: course.difficulty_level,
          status: course.status,
          category: typeof course.category === 'object' ? course.category?.id : course.category,
          is_public: course.is_public || false
        }
        
        await lmsAPI.updateCourse(courseId, updateData)
        toast.success('✅ Copied to Course Learning Objectives!', { timeout: 3000 })
      } catch (error) {
        console.error('Error updating course:', error)
        const errorMsg = error.response?.data?.error || error.message
        toast.error('Failed to update course: ' + errorMsg)
      }
    }

    const saveWithGeneratedContent = async () => {
      // User may have edited the generated content in Step 3
      // We need to save any edits back to the video
      
      try {
        // The video was created in generateContent(), get its ID
        // It should be in the stored createdVideo from Step 2
        const videoId = createdVideoId.value
        
        if (!videoId) {
          throw new Error('Video ID not found. Please try again.')
        }
        
        toast.info('Saving your edits...')
        
        // Update the video with edited content
        // Include required fields (title, course) even though not changed
        const updateData = {
          title: formData.value.title,
          course: formData.value.course,
          video_url: formData.value.video_url,
          video_type: formData.value.video_type,
          order: formData.value.order
        }
        
        // Add AI-generated content (edited or not)
        if (generatedContent.value.summary_paragraph) {
          updateData.summary_paragraph = generatedContent.value.summary_paragraph
        }
        if (generatedContent.value.summary_bullets) {
          updateData.summary_bullets = generatedContent.value.summary_bullets
        }
        if (generatedContent.value.objectives) {
          updateData.generated_objectives = generatedContent.value.objectives
        }
        if (generatedContent.value.quiz) {
          updateData.quiz_questions = generatedContent.value.quiz
        }
        if (generatedContent.value.chapters) {
          updateData.chapters = generatedContent.value.chapters
        }
        
        console.log('Updating video with data:', updateData)
        
        // Send update to backend
        await lmsAPI.updateVideo(videoId, updateData)
        
        toast.success('Video and AI-generated content saved successfully!', { timeout: 3000 })
        emit('video-created', { id: videoId })
        close()
        
      } catch (error) {
        console.error('Error saving edited content:', error)
        toast.error('Failed to save edits: ' + (error.response?.data?.error || error.message))
      }
    }

    const saveWithoutGeneration = async () => {
      generating.value = true
      try {
        const videoData = {
          course: formData.value.course,
          title: formData.value.title,
          description: formData.value.description,
          video_url: formData.value.video_url,
          video_type: formData.value.video_type,
          order: formData.value.order,
          duration_seconds: formData.value.duration_seconds,
          processing_status: 'COMPLETED'
        }
        
        const videoResponse = await lmsAPI.createVideo(videoData)
        
        emit('video-created', videoResponse.data)
        toast.success(`Video "${formData.value.title}" added successfully!`)
        close()
        
      } catch (error) {
        console.error('Error saving video:', error)
        const errorMsg = error.response?.data?.error || error.message || 'Failed to save video'
        toast.error(errorMsg)
      } finally {
        generating.value = false
      }
    }
    
    const close = () => {
      currentStep.value = 1
      vimeoMetadata.value = null
      vimeoId.value = null
      generatedContent.value = null
      createdVideoId.value = null
      formData.value = {
        course: null,
        title: '',
        description: '',
        video_url: '',
        video_type: 'MAIN_CONTENT',
        order: 0,
        duration_seconds: null
      }
      generateOptions.value = {
        summary_paragraph: true,
        summary_bullets: true,
        objectives: true,
        quiz: true,
        chapters: true
      }
      emit('close')
    }
    
    const formatDuration = (seconds) => {
      if (!seconds) return '0m'
      return lmsAPI.formatDuration(seconds)
    }
    
    return {
      currentStep,
      formData,
      vimeoMetadata,
      vimeoId,
      generateOptions,
      generatedContent,
      fetchingVimeo,
      generating,
      generatingTranscript,
      canProceedStep1,
      hasAnyOptionSelected,
      extractVimeoId,
      fetchVimeoMetadata,
      generateTranscript,
      nextStep,
      previousStep,
      selectAll,
      generateContent,
      saveWithGeneratedContent,
      saveWithoutGeneration,
      copyToCourseSummary,
      copyToCourseObjectives,
      close,
      formatDuration
    }
  }
}
</script>

<style scoped>
.wizard-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.75);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: var(--spacing-lg);
}

.wizard-modal {
  background: white;
  border-radius: 12px;
  max-width: 900px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  box-shadow: 0 12px 48px rgba(0, 0, 0, 0.3);
}

.close-btn {
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: transparent;
  border: none;
  font-size: 1.5rem;
  color: #6b7280;
  cursor: pointer;
  padding: 0.5rem;
  z-index: 10;
}

.close-btn:hover {
  color: #1f2937;
}

.wizard-header {
  padding: 2rem;
  border-bottom: 2px solid #e5e7eb;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-radius: 12px 12px 0 0;
}

.wizard-header h2 {
  margin: 0 0 1.5rem 0;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 1.75rem;
}

.step-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
}

.step {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  opacity: 0.5;
  transition: opacity 0.3s;
}

.step.active {
  opacity: 1;
}

.step.completed {
  opacity: 0.8;
}

.step-number {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
}

.step.active .step-number {
  background: white;
  color: #667eea;
}

.step.completed .step-number {
  background: rgba(255, 255, 255, 0.3);
}

.step-label {
  font-weight: 600;
  font-size: 0.95rem;
}

.step-divider {
  width: 60px;
  height: 2px;
  background: rgba(255, 255, 255, 0.3);
}

.wizard-body {
  padding: 2rem;
  min-height: 400px;
}

.wizard-step h3 {
  margin: 0 0 0.5rem 0;
  color: #1f2937;
  font-size: 1.5rem;
}

.step-description {
  color: #6b7280;
  margin: 0 0 2rem 0;
  font-size: 1rem;
}

.form-group {
  margin-bottom: 1.5rem;
}

.form-group label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 600;
  color: #374151;
}

.form-group small {
  display: block;
  margin-top: 0.25rem;
  color: #6b7280;
  font-size: 0.875rem;
}

.form-input,
.form-textarea,
.form-select {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 1rem;
  transition: border-color 0.2s;
}

.form-input:focus,
.form-textarea:focus,
.form-select:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.vimeo-input-group {
  display: flex;
  gap: 0.75rem;
}

.vimeo-input-group .form-input {
  flex: 1;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}

.vimeo-metadata {
  background: linear-gradient(135deg, #dbeafe 0%, #e0e7ff 100%);
  border: 2px solid #3b82f6;
  border-radius: 8px;
  padding: 1.25rem;
  margin: 1.5rem 0;
}

.metadata-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #1e40af;
  font-weight: 700;
  margin-bottom: 1rem;
  font-size: 1.1rem;
}

.metadata-header i {
  color: #10b981;
  font-size: 1.25rem;
}

.metadata-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.75rem;
}

.metadata-item {
  display: flex;
  gap: 0.5rem;
}

.metadata-item label {
  font-weight: 600;
  color: #1e40af;
}

.metadata-item span {
  color: #1e3a8a;
}

.metadata-item .warning {
  color: #d97706;
  font-weight: 600;
}

.transcript-status {
  background: #dbeafe;
  border-left: 4px solid #3b82f6;
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 2rem;
  display: flex;
  gap: 1rem;
  align-items: flex-start;
}

.transcript-status.warning {
  background: #fef3c7;
  border-left-color: #f59e0b;
}

.transcript-status i {
  font-size: 1.5rem;
  color: #3b82f6;
  margin-top: 0.25rem;
}

.transcript-status.warning i {
  color: #f59e0b;
}

.transcript-status.success {
  background: #d1fae5;
  border-left-color: #10b981;
}

.transcript-status.success i {
  color: #10b981;
}

.status-content {
  flex: 1;
}

.status-content strong {
  display: block;
  margin-bottom: 0.5rem;
  color: #1e40af;
}

.transcript-status.warning strong {
  color: #92400e;
}

.transcript-status.success strong {
  color: #065f46;
}

.transcript-help {
  margin: 0.75rem 0;
  color: #78350f;
  line-height: 1.5;
}

.transcript-actions {
  margin-top: 1rem;
}

.generating-note {
  margin: 0.75rem 0 0 0;
  padding: 0.75rem;
  background: rgba(251, 191, 36, 0.1);
  border-radius: 6px;
  color: #92400e;
  font-size: 0.9rem;
  line-height: 1.5;
}

.status-content p {
  margin: 0.75rem 0 0 0;
}

.generation-options {
  margin-top: 2rem;
}

.generation-options h4 {
  margin: 0 0 1rem 0;
  color: #1f2937;
}

.generation-option {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 1rem;
  background: #f9fafb;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  margin-bottom: 0.75rem;
  cursor: pointer;
  transition: all 0.2s;
}

.generation-option:hover {
  background: #f3f4f6;
  border-color: #667eea;
}

.generation-option input[type="checkbox"] {
  width: 20px;
  height: 20px;
  margin-top: 0.25rem;
  cursor: pointer;
}

.option-content {
  flex: 1;
}

.option-content strong {
  display: block;
  color: #1f2937;
  margin-bottom: 0.25rem;
}

.option-content small {
  color: #6b7280;
}

.generated-preview {
  background: #f0fdf4;
  border: 2px solid #10b981;
  border-radius: 8px;
  padding: 1.5rem;
  margin-top: 2rem;
}

.generated-preview h4 {
  margin: 0 0 1rem 0;
  color: #065f46;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.preview-section {
  margin-bottom: 1rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #d1fae5;
}

.preview-section:last-child {
  border-bottom: none;
  margin-bottom: 0;
  padding-bottom: 0;
}

.preview-section strong {
  display: block;
  color: #047857;
  margin-bottom: 0.5rem;
}

.preview-text {
  color: #065f46;
  line-height: 1.6;
  margin: 0;
}

.preview-list {
  margin: 0;
  padding-left: 1.5rem;
  color: #065f46;
}

.wizard-footer {
  padding: 1.5rem 2rem;
  border-top: 2px solid #e5e7eb;
  background: #f9fafb;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-radius: 0 0 12px 12px;
}

.footer-actions {
  display: flex;
  gap: 0.75rem;
}

.btn {
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.2s;
  font-size: 1rem;
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-primary {
  background: #667eea;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: #5568d3;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
}

.btn-success {
  background: #10b981;
  color: white;
}

.btn-success:hover:not(:disabled) {
  background: #059669;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
}

.btn-secondary {
  background: #e5e7eb;
  color: #374151;
}

.btn-secondary:hover:not(:disabled) {
  background: #d1d5db;
}

.btn-warning {
  background: #f59e0b;
  color: white;
  font-size: 0.9rem;
}

.btn-warning:hover:not(:disabled) {
  background: #d97706;
}

.btn-small {
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
}

@media (max-width: 768px) {
  .wizard-modal {
    max-width: 100%;
    max-height: 100vh;
    border-radius: 0;
  }
  
  .form-row,
  .metadata-grid {
    grid-template-columns: 1fr;
  }
  
  .step-indicator {
    flex-direction: column;
    gap: 0.5rem;
  }
  
  .step-divider {
    width: 2px;
    height: 30px;
  }
  
  .vimeo-input-group {
    flex-direction: column;
  }
  
  .footer-actions {
    flex-direction: column;
    width: 100%;
  }
  
  .footer-actions .btn {
    width: 100%;
    justify-content: center;
  }
}

/* Step 3: Review & Edit Styles */
.review-sections {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.review-section {
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 1.5rem;
}

.review-header {
  margin-bottom: 1rem;
  padding-bottom: 0.75rem;
  border-bottom: 2px solid #e5e7eb;
}

.review-header h4 {
  margin: 0 0 0.25rem 0;
  color: #1f2937;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.review-header small {
  color: #6b7280;
}

.review-textarea,
.bullet-input,
.objective-input,
.question-input,
.answer-input,
.chapter-input,
.explanation-textarea {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 1rem;
  font-family: inherit;
}

.review-textarea:focus,
.bullet-input:focus,
.objective-input:focus,
.question-input:focus,
.answer-input:focus,
.chapter-input:focus,
.explanation-textarea:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.actions-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px solid #e5e7eb;
}

.section-footer {
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #e5e7eb;
}

.char-count {
  color: #6b7280;
  font-size: 0.875rem;
}

.bullets-editor,
.objectives-editor,
.quiz-editor,
.chapters-editor {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.bullet-item,
.objective-item,
.chapter-item {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

.objective-number {
  font-weight: 600;
  color: #667eea;
  min-width: 30px;
}

.bullet-input,
.objective-input,
.chapter-input {
  flex: 1;
}

.time-input {
  width: 100px;
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
}

.remove-btn {
  background: #ef4444;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 0.5rem;
  cursor: pointer;
  transition: background 0.2s;
}

.remove-btn:hover {
  background: #dc2626;
}

.add-btn {
  background: #10b981;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 0.5rem 1rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: background 0.2s;
  align-self: flex-start;
}

.add-btn:hover {
  background: #059669;
}

.quiz-question {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 1rem;
}

.question-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
}

.question-input {
  margin-bottom: 1rem;
}

.answers-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
}

.answer-item {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

.answer-item input[type="radio"] {
  width: 18px;
  height: 18px;
}

.answer-input {
  flex: 1;
}

.explanation {
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px solid #e5e7eb;
}

.explanation label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 600;
  color: #374151;
  font-size: 0.9rem;
}
</style>

